import React, { useState } from 'react';
import { Camera, Check, Info, Image as ImageIcon, Link2, TrendingDown } from 'lucide-react';
import {
  PHOTOBOOTH_LOCATION as LOCATION,
  PHOTOBOOTH_PRIX_PHOTO as PRIX_PHOTO,
  PHOTOBOOTH_FORFAITS as FORFAITS,
  photoboothTotalReel,
} from '../../config/photoboothPacks';

/**
 * PhotoboothSelector — Choix du budget Photobooth
 * Modèle unique : le forfait = budget MAXIMUM. On facture le réel.
 *   150 € à la réservation (machine + galerie + lien + cadre personnalisable)
 *   + 0,50 €/photo imprimée, plafonné au budget choisi.
 *
 * Props: onFormulaChange(formula) avec
 *   formula = { type:'budget', key, label, prix_base:150, nb_photos, budget_max }
 */
export default function PhotoboothSelector({ onFormulaChange }) {
  // Forfaits "réels" (hors "aucune impression" qui reste le point de départ 150 €)
  const budgets = FORFAITS;
  const [selectedKey, setSelectedKey] = useState('f400');
  const selected = budgets.find(b => b.key === selectedKey) || budgets[0];
  const maxSlider = Math.max(selected.nb, 50);
  const [nbSimul, setNbSimul] = useState(Math.min(100, maxSlider));

  function emit(forfait) {
    if (onFormulaChange) onFormulaChange({
      type: 'budget',
      key: forfait.key,
      label: forfait.nb === 0 ? 'Photobooth — Aucune impression' : `Photobooth — Budget ${forfait.label}`,
      prix_base: LOCATION,          // 150 € payés à la réservation
      nb_photos: forfait.nb,
      budget_max: forfait.max,
    });
  }

  function handleSelect(key) {
    const f = budgets.find(b => b.key === key);
    setSelectedKey(key);
    setNbSimul(Math.min(nbSimul, Math.max(f.nb, 50)));
    emit(f);
  }

  React.useEffect(() => { emit(selected); /* init */ // eslint-disable-next-line
  }, []);

  const simulTotal = photoboothTotalReel(Math.min(nbSimul, selected.nb || nbSimul));

  return (
    <div data-testid="photobooth-selector">
      {/* ─── Titre ─── */}
      <div className="flex items-center gap-2 mb-3">
        <Camera size={16} style={{ color: 'var(--teal)' }} />
        <p className="text-sm font-bold" style={{ color: 'var(--ink)' }}>
          Choisissez votre budget Photobooth
        </p>
      </div>

      {/* ─── Base 150 € ─── */}
      <div className="flex items-center justify-between p-3 rounded-2xl mb-3"
        style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.25)' }}>
        <div className="min-w-0">
          <p className="text-xs font-bold mb-0.5" style={{ color: '#0B4B4E' }}>Location à la réservation</p>
          <p className="text-xs" style={{ color: 'rgba(11,75,78,0.7)' }}>
            Machine installée + galerie en ligne + lien + cadre personnalisable
          </p>
        </div>
        <div className="text-right flex-shrink-0 ml-3">
          <p className="text-2xl font-bold price-gold">{LOCATION}€</p>
          <p className="text-xs" style={{ color: 'rgba(11,75,78,0.6)' }}>acompte</p>
        </div>
      </div>

      {/* ─── Principe ─── */}
      <div className="flex items-start gap-2 p-3 rounded-xl mb-3"
        style={{ background: 'rgba(31,157,122,0.07)', border: '1px solid rgba(31,157,122,0.2)' }}>
        <TrendingDown size={13} style={{ color: '#1F9D7A', flexShrink: 0, marginTop: 1 }} />
        <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
          Vous ne payez que vos <strong style={{ color: 'var(--ink)' }}>impressions réelles</strong> ({PRIX_PHOTO.toFixed(2).replace('.', ',')}€ / photo),
          jamais plus que votre budget. Le solde est réglé <strong style={{ color: 'var(--ink)' }}>après l'événement</strong>.
        </p>
      </div>

      {/* ─── Grille des budgets ─── */}
      <p className="text-xs font-bold mb-2 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
        Budget maximum
      </p>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {budgets.map(b => {
          const isSelected = selectedKey === b.key;
          return (
            <button
              key={b.key}
              onClick={() => handleSelect(b.key)}
              data-testid={`budget-${b.key}`}
              className="relative p-3 rounded-2xl text-left transition-all"
              style={{
                background: isSelected ? '#0B1E20' : '#FFFFFF',
                border: `1.5px solid ${isSelected ? b.color : 'var(--border)'}`,
                boxShadow: isSelected ? `0 4px 16px ${b.color}30` : 'none',
                cursor: 'pointer',
              }}>
              {b.popular && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: b.color, color: '#0B1E20', whiteSpace: 'nowrap' }}>
                  ⭐ Populaire
                </span>
              )}
              <p className="text-xs font-semibold mb-1"
                style={{ color: isSelected ? 'rgba(255,255,255,0.65)' : 'var(--text-muted)' }}>
                {b.nb === 0 ? 'Aucune impression' : `jusqu'à ${b.nb} photos`}
              </p>
              <p className="text-xl font-bold" style={{ color: isSelected ? b.color : 'var(--ink)' }}>
                {b.max}€
              </p>
              <p className="text-xs mt-0.5"
                style={{ color: isSelected ? b.color : 'var(--text-muted)', opacity: isSelected ? 0.85 : 1 }}>
                {b.nb === 0 ? 'location seule' : 'budget max'}
              </p>
              {isSelected && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ background: b.color }}>
                  <Check size={11} style={{ color: '#0B1E20' }} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* ─── Ce qui est inclus ─── */}
      <div className="space-y-1.5 mb-3">
        {[
          { icon: Check, t: 'Installation du photobooth' },
          { icon: Check, t: 'Accès illimité pendant l\'événement' },
          { icon: Link2, t: 'Galerie en ligne privée + lien dans tous les cas' },
          { icon: ImageIcon, t: 'Cadre de la photo personnalisable' },
          { icon: Check, t: 'Paiement des impressions au réel après l\'événement' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            <item.icon size={12} style={{ color: '#1F9D7A', flexShrink: 0 }} />
            {item.t}
          </div>
        ))}
      </div>

      {/* ─── Simulateur ─── */}
      {selected.nb > 0 && (
        <div className="rounded-2xl p-4" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-3 mb-3">
            <label className="text-xs font-medium shrink-0" style={{ color: 'var(--text-muted)' }}>
              Impressions estimées :
            </label>
            <input
              type="range" min={0} max={selected.nb} step={10}
              value={Math.min(nbSimul, selected.nb)}
              onChange={e => setNbSimul(Number(e.target.value))}
              data-testid="photobooth-simulateur"
              className="flex-1" style={{ accentColor: 'var(--teal)' }} />
            <span className="text-sm font-bold w-16 text-right" style={{ color: 'var(--ink)' }}>
              {Math.min(nbSimul, selected.nb)} photos
            </span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl"
            style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
            <span className="text-xs font-semibold" style={{ color: '#0B4B4E' }}>
              Coût réel estimé ({LOCATION}€ + impressions)
            </span>
            <span className="text-xl font-bold price-gold" data-testid="photobooth-simul-total">{simulTotal}€</span>
          </div>
          <div className="flex items-start gap-2 mt-2">
            <Info size={11} style={{ color: 'var(--teal)', flexShrink: 0, marginTop: 2 }} />
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Vous réglez {LOCATION}€ maintenant, le solde après l'événement selon le nombre réel de photos — plafonné à {selected.max}€.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
